import type { ICustomerService } from '../interface/ICustomerService';
import type { Customer, CustomerListResponse, UpdateCustomerRequest, GetUsersLinkedToCustomerResponse } from '../../models/customerModel';
import type { CreateCustomerRequest, CreateCustomerResponse } from '../../models/createCustomer.types';
import type { CustomerPartnerDetails } from '../../models/customerPartnerDetails.types';
import type { SuluAutomationStep } from '../../models/suluAutomation.types';
import type { UpdateJiraTicketResult } from '../../models/customerWorkspace.types';
import type { IHttpWrapperService } from '../interface/IHttpWrapperService';
import { container, TYPES } from '../inversify.config';
import type { AxiosResponse } from 'axios';
import type { ApiResponse } from '../../models/ApiResponse';
import { isSuccessResponse } from '../../models/ApiResponse';
import { injectable } from 'inversify';
import 'reflect-metadata';
import {
    normalizeCustomerApiRecord,
    normalizeCustomerListResponse,
} from '../../utils/customerApiNormalize';
import { normalizeCustomerPartnerDetails } from '../../utils/customerRequestIntakeNormalize';
import { normalizeSuluAutomationPayload } from '../../utils/suluAutomationNormalize';


export class CustomerService implements ICustomerService {
    private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

    async GetPartnerDetails(id: string): Promise<CustomerPartnerDetails> {
        const response: AxiosResponse<ApiResponse<unknown>> =
            await this.httpService.getRequest<unknown>(`customer/getpartnerdetails?id=${id}`);

        if (isSuccessResponse(response.data) && response.data.data != null) {
            return normalizeCustomerPartnerDetails(response.data.data);
        }

        throw new Error(response.data?.message || 'Failed to fetch partner details');
    }

    async GetSuluAutomation(id: string): Promise<SuluAutomationStep[]> {
        const response: AxiosResponse<ApiResponse<unknown>> =
            await this.httpService.getRequest<unknown>(`customer/getsuluautomation?id=${id}`);

        if (isSuccessResponse(response.data)) {
            return normalizeSuluAutomationPayload(response.data.data);
        }

        throw new Error(response.data?.message || 'Failed to fetch SULU setup');
    }

    async ReprocessSulu(id: string): Promise<void> {
        const response: AxiosResponse<ApiResponse<unknown>> =
            await this.httpService.postRequest<unknown, undefined>(
                `customer/sulu/reprocesssulu?id=${id}`,
                undefined
            );

        if (!isSuccessResponse(response.data)) {
            throw new Error(response.data?.message || 'Failed to reprocess SULU setup');
        }
    }

    async GetCustomerById(id: string): Promise<Customer | null> {
        try {
            const response: AxiosResponse<ApiResponse<Customer>> = await this.httpService.getRequest<Customer>(`customer/domain/getcustomerdetails?id=${id}`);
            
            if (isSuccessResponse(response.data)) {
                const data = response.data.data;
                if (!data || typeof data !== 'object') {
                    return null;
                }
                return normalizeCustomerApiRecord(data as Customer);
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    }

    async GetAllCustomers(pageNumber: number = 1, pageSize: number = 10): Promise<CustomerListResponse> {
        try {
            const endpoint = `customer/domain/getallcustomers?pageNumber=${pageNumber}&pageSize=${pageSize}`;
            const response: AxiosResponse<ApiResponse<CustomerListResponse>> = await this.httpService.getRequest<CustomerListResponse>(endpoint);
            
            if (!response) {
                throw new Error('No response received from server');
            }
            
            if (isSuccessResponse(response.data)) {
                return normalizeCustomerListResponse(response.data.data);
            } else {
                throw new Error(response.data.message || 'Failed to fetch customers');
            }
        } catch (error) {
            throw error;
        }
    }

    async GetAllCustomersBoardView(): Promise<CustomerListResponse> {
        try {
            const endpoint = `customer/domain/getallcustomersboardview`;
            const response: AxiosResponse<ApiResponse<CustomerListResponse>> = await this.httpService.getRequest<CustomerListResponse>(endpoint);
            
            if (!response) {
                throw new Error('No response received from server');
            }
            
            if (isSuccessResponse(response.data)) {
                return normalizeCustomerListResponse(response.data.data);
            } else {
                throw new Error(response.data.message || 'Failed to fetch customers for board view');
            }
        } catch (error) {
            throw error;
        }
    }

    async CreateCustomer(customer: CreateCustomerRequest): Promise<CreateCustomerResponse> {
        try {
            const response: AxiosResponse<ApiResponse<CreateCustomerResponse>> =
                await this.httpService.postRequest<CreateCustomerResponse, CreateCustomerRequest>(
                    'customer/domain/createcustomer',
                    customer
                );

            if (isSuccessResponse(response.data)) {
                return response.data.data ?? customer;
            } else {
                throw new Error(response.data.message || 'Failed to create customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async UpdateCustomer(id: string, customerData: UpdateCustomerRequest): Promise<void> {
        try {
            const endpoint = `customer/domain/updatecustomer/${id}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.putRequest<void, UpdateCustomerRequest>(endpoint, customerData);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to update customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async SubmitForDevOpsApproval(id: string): Promise<void> {
        try {
            const endpoint = `customer/domain/submitfordevopsapproval?id=${id}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, undefined>(endpoint, undefined);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to submit for DevOps approval');
            }
        } catch (error) {
            throw error;
        }
    }

    async ApproveDomain(customerId: string): Promise<void> {
        try {
            const endpoint = `domain/approvedomain?customerid=${customerId}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, undefined>(endpoint, undefined);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to approve domain');
            }
        } catch (error) {
            throw error;
        }
    }

    async RejectDomain(customerId: string): Promise<void> {
        try {
            const endpoint = `domain/rejectdomain?customerid=${customerId}`;
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, undefined>(endpoint, undefined);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to reject domain');
            }
        } catch (error) {
            throw error;
        }
    }

    // DevOps confirms the domain exists in OTM: `POST domain/confirmdomaincreation` with a
    // ConfirmDomainCreationCommand body. The API answers 200 with IsSuccess=false on failure
    // (for example when the customer is not in WaitingforDomaincreation).
    async ConfirmDomainCreation(customerId: string, partnerIdentifier: string): Promise<void> {
        const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<
            void,
            { customerId: string; partnerIdentifier: string }
        >('domain/confirmdomaincreation', { customerId, partnerIdentifier });

        if (!isSuccessResponse(response.data)) {
            throw new Error(response.data.message || 'Failed to confirm domain creation');
        }
    }

    // `POST customer/updatejira` with { customerId, jira }. The API echoes { customerId, jira } and
    // does not resolve an epic, so jiraEpic is always null here. Failures come back as 200 with
    // IsSuccess=false (for example "Jira is required." or "Customer with Id … not found.").
    async UpdateJiraTicket(customerId: string, ticketKey: string): Promise<UpdateJiraTicketResult> {
        const response: AxiosResponse<ApiResponse<{ customerId: string; jira: string | null }>> =
            await this.httpService.postRequest<
                { customerId: string; jira: string | null },
                { customerId: string; jira: string }
            >('customer/updatejira', { customerId, jira: ticketKey });

        if (!isSuccessResponse(response.data)) {
            throw new Error(response.data.message || 'Failed to save Jira ticket');
        }

        const savedKey = response.data.data?.jira?.trim() || ticketKey;
        return { jira: { key: savedKey }, jiraEpic: null };
    }

    // `POST customer/deletejira` with { customerId }. Failures come back as 200 with IsSuccess=false.
    async DeleteJiraTicket(customerId: string): Promise<void> {
        const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<
            void,
            { customerId: string }
        >('customer/deletejira', { customerId });

        if (!isSuccessResponse(response.data)) {
            throw new Error(response.data.message || 'Failed to remove Jira ticket');
        }
    }

    async GetUsersLinkedToCustomer(customerId: string): Promise<GetUsersLinkedToCustomerResponse> {
        try {
            const endpoint = `customer/domain/getuserslinkedtocustomer/${customerId}`;
            const response: AxiosResponse<ApiResponse<GetUsersLinkedToCustomerResponse>> = await this.httpService.getRequest<GetUsersLinkedToCustomerResponse>(endpoint);
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch users linked to customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async LinkUsersToCustomer(customerId: string, userIds: string[]): Promise<void> {
        try {
            const endpoint = 'customer/domain/linkuserstocustomer';
            const requestBody = {
                customerId,
                userIds
            };
            const response: AxiosResponse<ApiResponse<void>> = await this.httpService.postRequest<void, typeof requestBody>(endpoint, requestBody);
            
            if (!isSuccessResponse(response.data)) {
                throw new Error(response.data.message || 'Failed to link users to customer');
            }
        } catch (error) {
            throw error;
        }
    }

    async ValidateDataEntryStage(partnerCode: string): Promise<{
        isSuccess: boolean;
        data: {
            isValid: boolean;
            entities: string[];
        };
        message: string;
    }> {
        try {
            const endpoint = `customer/domain/validatedataentrystage?partnerCode=${partnerCode}`;
            const response: AxiosResponse<ApiResponse<{ isValid: boolean; entities: string[] }>> = 
                await this.httpService.getRequest<{ isValid: boolean; entities: string[] }>(endpoint);
            
            return {
                isSuccess: response.data.isSuccess,
                data: response.data.data,
                message: response.data.message || ''
            };
        } catch (error) {
            throw error;
        }
    }

    async MarkComplete(customerId: string): Promise<void> {
        const endpoint = `customer/markcomplete?id=${customerId}`;
        const response: AxiosResponse<ApiResponse<void>> =
            await this.httpService.postRequest<void, undefined>(endpoint, undefined);

        if (!isSuccessResponse(response.data)) {
            throw new Error(response.data.message || 'Failed to mark onboarding complete');
        }
    }

    async UpdateOnboardingStatus(customerId: string, status: string): Promise<{
        isSuccess: boolean;
        data: any;
        message: string;
    }> {
        try {
            const endpoint = `customer/domain/onboardingstatusupdate/${customerId}?onboardingStatus=${status}`;
            const response: AxiosResponse<ApiResponse<any>> = 
                await this.httpService.putRequest<any, undefined>(endpoint, undefined);
            
            return {
                isSuccess: response.data.isSuccess,
                data: response.data.data,
                message: response.data.message || ''
            };
        } catch (error) {
            throw error;
        }
    }
}
